import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { formatRating } from "@/utils/movies";
import { useFavorites } from "@/hooks/useFavorites";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const slides = useMemo(
    () => movies.filter((m) => m.featured && m.banner).slice(0, 5),
    [movies],
  );
  const [current, setCurrent] = useState(0);
  const { toggle, isFavorite } = useFavorites();

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const active = slides[current];

  return (
    <section className="relative w-full" aria-label="焦点影视推荐">
      <div className="relative h-[420px] w-full overflow-hidden md:h-[520px]">
        {slides.map((movie, index) => (
          <img
            key={movie.id}
            src={movie.banner}
            alt={`${movie.title} 焦点海报`}
            title={`${movie.title} 高清影视`}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
              index === current ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/55 to-foreground-950/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 to-transparent" />

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full max-w-[1280px] flex-col justify-end px-4 pb-12 md:px-6 md:pb-16">
            <div key={active.id} className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-background-50">
                <i className="ri-fire-fill" aria-hidden="true" />
                本周焦点
              </span>
              <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-background-50 md:text-5xl">
                {active.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-background-100">
                <span className="inline-flex items-center gap-1 font-label font-bold text-accent-300">
                  <i className="ri-star-fill text-xs" aria-hidden="true" />
                  {formatRating(active.rating)}
                </span>
                <span>{active.year}</span>
                <span aria-hidden="true">·</span>
                <span>{active.region}</span>
                <span aria-hidden="true">·</span>
                <span>{active.genres.join(" / ")}</span>
                <span aria-hidden="true">·</span>
                <span>{active.duration}</span>
              </div>
              <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                {active.synopsis}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-lg" aria-hidden="true" />
                  查看详情
                </Link>
                <button
                  type="button"
                  onClick={() => toggle(active.id)}
                  className={`inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-5 text-sm font-semibold transition-colors ${
                    isFavorite(active.id)
                      ? "border-accent-400 bg-accent-500 text-background-50"
                      : "border-background-50/40 text-background-50 hover:bg-background-50/10"
                  }`}
                >
                  <i
                    className={isFavorite(active.id) ? "ri-bookmark-fill text-lg" : "ri-bookmark-line text-lg"}
                    aria-hidden="true"
                  />
                  {isFavorite(active.id) ? "已收藏" : "加入收藏"}
                </button>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrent((prev) => (prev - 1 + slides.length) % slides.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
                aria-label="上一张"
              >
                <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => setCurrent((prev) => (prev + 1) % slides.length)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
                aria-label="下一张"
              >
                <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
              </button>
              <div className="ml-2 flex items-center gap-2">
                {slides.map((movie, index) => (
                  <button
                    key={movie.id}
                    type="button"
                    onClick={() => setCurrent(index)}
                    aria-label={`切换到 ${movie.title}`}
                    className={`h-1.5 cursor-pointer rounded-full transition-all ${
                      index === current ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}